import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  EXPORTER_COST_MAPPINGS_CHANGED_EVENT,
  ExporterCostCategory,
  getReceiverCostConcepts,
  ReceiverCostConcept,
  setReceiverCostExporterCategories,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';
import { OFFICIAL_COST_CATEGORY_LABELS } from 'common/src/api/receiver-costs';

const ALL = 'all';
const UNCLASSIFIED = 'unclassified';

const formatUsd = (value: number) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

interface ReceiverCostConceptsSectionProps {
  exporterId: number;
  categories: ExporterCostCategory[];
  /** Called after a mapping is saved here. */
  onChanged?: () => void;
}

/**
 * Every original cost name the exporter's clients have used (any season),
 * with its current exporter category, filterable and editable. Changes apply
 * to all settlements from the first Estándar Exportadora season onwards.
 */
export const ReceiverCostConceptsSection = ({
  exporterId,
  categories,
  onChanged,
}: ReceiverCostConceptsSectionProps) => {
  const { t } = useTranslation('settings');
  const [concepts, setConcepts] = useState<ReceiverCostConcept[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [receiverFilter, setReceiverFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [search, setSearch] = useState('');

  const activeCategories = useMemo(
    () => categories.filter((category) => !category.archivedAt),
    [categories]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConcepts(await getReceiverCostConcepts(exporterId));
    } catch (error) {
      console.error('Error loading receiver cost concepts:', error);
      toast.error(t('Error al cargar los costos originales.'));
    } finally {
      setLoading(false);
    }
  }, [exporterId, t]);

  useEffect(() => {
    load();
    window.addEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, load);
    return () =>
      window.removeEventListener(EXPORTER_COST_MAPPINGS_CHANGED_EVENT, load);
  }, [load]);

  const receivers = useMemo(
    () =>
      Array.from(
        new Map(
          concepts.map((concept) => [
            concept.exporterReceiverId,
            concept.receiverName,
          ])
        )
      ).sort((a, b) => a[1].localeCompare(b[1], 'es')),
    [concepts]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toUpperCase();
    return concepts.filter(
      (concept) =>
        (receiverFilter === ALL ||
          String(concept.exporterReceiverId) === receiverFilter) &&
        (categoryFilter === ALL ||
          (categoryFilter === UNCLASSIFIED
            ? concept.categoryId === null
            : String(concept.categoryId) === categoryFilter)) &&
        (!term || concept.sampleOriginalName.toUpperCase().includes(term))
    );
  }, [categoryFilter, concepts, receiverFilter, search]);

  const conceptKey = (concept: ReceiverCostConcept) =>
    `${concept.exporterReceiverId}:${concept.normalizedConcept}`;

  const handleChange = async (
    concept: ReceiverCostConcept,
    categoryId: number | null
  ) => {
    if (concept.categoryId === categoryId) return;
    const key = conceptKey(concept);
    setSavingKey(key);
    try {
      await setReceiverCostExporterCategories(concept.exporterReceiverId, [
        { name: concept.sampleOriginalName, categoryId },
      ]);
      setConcepts((current) =>
        current.map((item) =>
          conceptKey(item) === key ? { ...item, categoryId } : item
        )
      );
      toast.success(t('Categoría actualizada.'));
      onChanged?.();
    } catch (error: any) {
      console.error('Error saving exporter cost category:', error);
      toast.error(error?.message || t('No se pudo guardar la categoría.'));
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div>
      <p className='text-lg font-bold'>{t('Costos originales por cliente')}</p>
      <p className='mb-3 text-sm text-default-400'>
        {t(
          'Todos los conceptos de costos que tus clientes han usado y la categoría propia a la que se asignan. Los cambios se aplican a todas las liquidaciones desde la temporada 2026-2027.'
        )}
      </p>

      <div className='mb-3 grid grid-cols-1 gap-3 md:grid-cols-3'>
        <Select
          size='sm'
          label={t('Cliente')}
          selectedKeys={[receiverFilter]}
          onSelectionChange={(keys) => {
            const value = Array.from(keys)[0];
            if (typeof value === 'string') setReceiverFilter(value);
          }}
        >
          {[
            <SelectItem key={ALL} value={ALL}>
              {t('Todos los clientes')}
            </SelectItem>,
            ...receivers.map(([id, name]) => (
              <SelectItem key={String(id)} value={String(id)}>
                {name}
              </SelectItem>
            )),
          ]}
        </Select>
        <Select
          size='sm'
          label={t('Categoría propia')}
          selectedKeys={[categoryFilter]}
          onSelectionChange={(keys) => {
            const value = Array.from(keys)[0];
            if (typeof value === 'string') setCategoryFilter(value);
          }}
        >
          {[
            <SelectItem key={ALL} value={ALL}>
              {t('Todas')}
            </SelectItem>,
            <SelectItem key={UNCLASSIFIED} value={UNCLASSIFIED}>
              {t(UNCLASSIFIED_EXPORTER_COST_LABEL)}
            </SelectItem>,
            ...categories.map((category) => (
              <SelectItem key={String(category.id)} value={String(category.id)}>
                {category.archivedAt
                  ? `${category.name} (${t('archivada')})`
                  : category.name}
              </SelectItem>
            )),
          ]}
        </Select>
        <Input
          size='sm'
          label={t('Buscar concepto')}
          value={search}
          onValueChange={setSearch}
          isClearable
          onClear={() => setSearch('')}
        />
      </div>

      {loading ? (
        <Spinner size='sm' />
      ) : (
        <Table aria-label={t('Costos originales por cliente')} removeWrapper>
          <TableHeader>
            <TableColumn>{t('Cliente')}</TableColumn>
            <TableColumn>{t('Concepto original')}</TableColumn>
            <TableColumn>{t('Costo Estándar FruitMarkets')}</TableColumn>
            <TableColumn>{t('Liquidaciones')}</TableColumn>
            <TableColumn>{t('Total USD')}</TableColumn>
            <TableColumn>{t('Categoría propia')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('No hay conceptos con estos filtros.')}>
            {filtered.map((concept) => {
              const key = conceptKey(concept);
              return (
                <TableRow key={key}>
                  <TableCell>{concept.receiverName}</TableCell>
                  <TableCell>{concept.sampleOriginalName}</TableCell>
                  <TableCell>
                    {concept.officialCategory
                      ? t(
                          OFFICIAL_COST_CATEGORY_LABELS[
                            concept.officialCategory
                          ]
                        )
                      : '—'}
                  </TableCell>
                  <TableCell>{concept.settlementCount}</TableCell>
                  <TableCell>{formatUsd(concept.totalUsd)}</TableCell>
                  <TableCell>
                    <Select
                      size='sm'
                      className='min-w-56'
                      aria-label={`${t('Categoría propia')} ${concept.sampleOriginalName}`}
                      isDisabled={savingKey === key}
                      selectedKeys={[
                        concept.categoryId === null
                          ? UNCLASSIFIED
                          : String(concept.categoryId),
                      ]}
                      onSelectionChange={(keys) => {
                        const value = Array.from(keys)[0];
                        if (typeof value !== 'string') return;
                        handleChange(
                          concept,
                          value === UNCLASSIFIED ? null : Number(value)
                        );
                      }}
                    >
                      {[
                        <SelectItem key={UNCLASSIFIED} value={UNCLASSIFIED}>
                          {t(UNCLASSIFIED_EXPORTER_COST_LABEL)}
                        </SelectItem>,
                        ...categories
                          .filter(
                            (category) =>
                              !category.archivedAt ||
                              category.id === concept.categoryId
                          )
                          .map((category) => (
                            <SelectItem
                              key={String(category.id)}
                              value={String(category.id)}
                            >
                              {category.name}
                            </SelectItem>
                          )),
                      ]}
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {!loading && activeCategories.length === 0 && (
        <p className='mt-2 text-xs text-default-400'>
          {t('Crea categorías arriba para poder asignarlas.')}
        </p>
      )}
    </div>
  );
};
